import { Trans, useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { SCROLL_COLLAPSE_EASING_DURATION_CLASS } from '~/components/ScrollCollapse/constants/classes';
import { useScrollCollapseContext } from '~/components/ScrollCollapse/hooks/useScrollCollapseContext';
import { cn } from '~/lib/cn';
import { useIsContactInfoEnabled } from '~/modules/artist-roster/hooks/useIsContactInfoEnabled/useIsContactInfoEnabled';
import { getCountryName } from '~/modules/artist-roster/lib/artists';
import { DataToolLinks } from '~/modules/artist-roster/pages/artist-details/components/ArtistHeader/components/DataToolLinks';
import { SocialLinks } from '~/modules/artist-roster/pages/artist-details/components/ArtistHeader/components/SocialLinks';
import { doLabelsHavePermission } from '~/modules/artist-roster/pages/roster/utils/doLabelsHavePermission';
import type { ArtistDetails } from '~/modules/artist-roster/types';
import { useUser } from '~/modules/auth/hooks/useUser';

import { ContactInfoPopover } from './ContactInfoPopover';

interface ArtistHeaderDetailsProps {
  artistData?: ArtistDetails;
}

export function ArtistHeaderDetails({ artistData }: ArtistHeaderDetailsProps) {
  const { t } = useTranslation('roster');
  const { t: tGrammar } = useTranslation('grammar');
  const { enabled: isContactInfoEnabled } = useIsContactInfoEnabled();
  const { scrollProgress, getInterpolatedValue } = useScrollCollapseContext();
  const isHeaderCollapsed = scrollProgress > 1;

  if (!artistData) {
    return null;
  }

  const labelsLength = artistData.labels.length;
  const { permissions } = useUser();
  const hasViewContactInfoPermission = doLabelsHavePermission(
    artistData.labels,
    permissions,
    'PARTY_CONTACT_INFO.VIEW',
  );
  const countryOfOrigin = artistData.countryOfOriginISO
    ? getCountryName(artistData.countryOfOriginISO, navigator.language)
    : undefined;

  return (
    <div
      className={cn(
        'mb-lg flex w-full justify-center will-change-[gap]',
        SCROLL_COLLAPSE_EASING_DURATION_CLASS,
      )}
      style={{
        // Transition from "gap-2xl" to "gap-lg".
        gap: getInterpolatedValue(40, 24),
        // Transition from "mb-lg" to "12px".
        marginBottom: getInterpolatedValue(24, 12),
      }}
    >
      <div
        className={cn(
          'flex aspect-square items-center justify-center will-change-[height] bg-level-03',
          SCROLL_COLLAPSE_EASING_DURATION_CLASS,
          {
            '!h-[56px]': isHeaderCollapsed,
          },
        )}
        // Transition from "h-[148px]" to "h-[80px]".
        style={{ height: getInterpolatedValue(148, 80) }}
      >
        {artistData.image ? (
          <img
            src={artistData.image}
            alt={artistData.name}
            className="size-full object-cover"
          />
        ) : (
          <Icon
            type="user"
            variant="light"
            aria-label={t('labels.emptyAvatar')}
          />
        )}
      </div>
      <div className="flex grow flex-col items-start justify-center self-end text-body-sm">
        <div
          className={cn(
            'flex items-center gap-sm will-change-[margin]',
            SCROLL_COLLAPSE_EASING_DURATION_CLASS,
          )}
          // Transition from "mb-sm" to "mb-0".
          style={{ marginBottom: getInterpolatedValue(8, 0) }}
        >
          <h1
            className={cn(
              'font-bold will-change-[font-size]',
              SCROLL_COLLAPSE_EASING_DURATION_CLASS,
            )}
            // Transition from "text-display-md" to "text-headline-sm".
            style={{ fontSize: getInterpolatedValue(32, 18) }}
          >
            {artistData.name}
          </h1>
          {/* @TODO WMG1-572 - Put back in once back end data is fixed. */}
          {/* {artistData.isCrossover ? <ArtistCrossoverIcon /> : null} */}
          {isContactInfoEnabled && hasViewContactInfoPermission ? (
            <div>
              <ContactInfoPopover
                partyId={artistData.partyId}
                entityType={artistData.entityType}
              />
            </div>
          ) : null}
        </div>
        <div
          className={cn(
            'flex gap-sm will-change-[margin-bottom]',
            SCROLL_COLLAPSE_EASING_DURATION_CLASS,
          )}
          // Transition from "mb-sm" to "mb-0".
          style={{ marginBottom: getInterpolatedValue(8, 0) }}
        >
          {countryOfOrigin ? (
            <>
              <span aria-label={t('details.countryOfOrigin')}>
                {countryOfOrigin}
              </span>
              <div className="border-s-px border-emphasis-subtle" />
            </>
          ) : null}
          <span aria-label={t('details.rosters')}>
            <Trans
              i18nKey="details.roster"
              t={t}
              values={{
                count: labelsLength,
                list: labelsLength
                  ? artistData.labels
                      .map((label) => label.name)
                      .join(tGrammar('commaSpace'))
                  : '--',
              }}
              components={{
                span: labelsLength ? <></> : <span className="text-subtle" />,
              }}
            />
          </span>
        </div>
        <div
          className={cn(
            'flex w-full items-center justify-between will-change-[height,opacity]',
            SCROLL_COLLAPSE_EASING_DURATION_CLASS,
            {
              'h-[37px] opacity-full': !isHeaderCollapsed,
              'pointer-events-none h-0 opacity-none': isHeaderCollapsed,
            },
          )}
        >
          <SocialLinks artistData={artistData} />
          <DataToolLinks artistData={artistData} />
        </div>
      </div>
    </div>
  );
}

ArtistHeaderDetails.displayName = 'ArtistHeaderDetails';
